import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import TaskProgress, { ExportPDFFunction } from './TaskProgress';
// 移除 Header 引入
// 顶部引入 Markdown 组件，统一 markdown 渲染
import { Markdown } from '../components/markdown/markdown';
// @ts-ignore
import jsPDF from 'jspdf';
// @ts-ignore
import html2canvas from 'html2canvas';
import { message, notification, Space, Button, Modal, Form, Input, Select } from 'antd';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import copy from 'copy-to-clipboard';

// CSS样式已移除，将使用内联方式

interface AppData {
  app_id: number;
  app_name: string;
  app_description: string;
  app_type: string;
  status: string;
  app_json: any;
  workspace_id: number;
  creator_user_id: number;
  create_time: string;
  update_time: string;
  publish_uuid?: string;
  published_at?: string;
}

interface Version {
  version_id: number;
  version_name: string;
  version_code: string;
  version_description: string;
  version_status: string;
  version_create_time: string;
  version_json: {
    name: string;
    type: string;
    configs: {
      agents: string[];
      tasks: string[];
    };
    language: string;
    works: {
      name: string;
      description: string;
    }[];
    parsed_workflow?: Array<{
      work_name: string;
      work_description: string;
      agent_role: string;
      agent_goal: string;
      agent_backstory: string;
      task_description: string;
      task_expected_output: string;
    }>;
  };
}

interface RunRecord {
  record_id: number;
  app_id: number;
  version_id: number;
  user_id: number;
  input_params?: {
    task_input: string;
    crew_json: any;
  };
  output_params: {
    [key: string]: any;
  };
  run_time: string;
  result: string;
}

interface ApiError {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
}

interface ApiErrorResponse {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
}

interface AgentOutput {
  name: string;
  type: string;
  configs: {
    agents: string[];
    tasks: string[];
  };
  language: string;
  works: {
    name: string;
    description: string;
  }[];
}

interface SaveVersionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  configs: any;
  appId: string;
}

type TabType = 'scene' | 'records';

interface WorkflowResponse {
  data: {
    outputs: AgentOutput;
  };
}

interface RunModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRun: (taskInput: string) => void;
  versionData: Version | null;
}

interface VersionDetail extends Version {
  version_json: {
    name: string;
    type: string;
    configs: {
      agents: string[];
      tasks: string[];
    };
    language: string;
    works: {
      name: string;
      description: string;
    }[];
    parsed_workflow?: Array<{
      work_name: string;
      work_description: string;
      agent_role: string;
      agent_goal: string;
      agent_backstory: string;
      task_description: string;
      task_expected_output: string;
    }>;
  };
}

interface RecordDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  record: RunRecord | null;
}

interface VersionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  version: VersionDetail | null;
}

interface VersionData {
  app_id: string;
  version_name: string;
  version_code: string;
  version_description: string;
  version_json: {
    outputs?: AgentOutput;
  };
}

interface WorkLog {
  type: 'log';
  content: string;
}

interface WorkTask {
  timestamp: string;
  task_name: string;
  task: string;
  agent: string;
  status: 'started' | 'completed';
  content: string;
}

interface WorkProgress {
  type: 'work';
  timestamp: string;
  task_name: string;
  task: string;
  agent: string;
  status: 'started' | 'completed';
  content: string;
}

interface WorkResult {
  type: 'result';
  content: string;
}

type WorkResponse = WorkLog | WorkProgress | WorkResult;

interface RunProgress {
  logs: string[];
  tasks: WorkTask[];
  result?: string;
  progress: number;
}

interface RunRecordResponse {
  record_id: number;
  app_id: number;
  version_id: number;
  user_id: number;
  input_params: {
    task_input: string;
    crew_json: any;
  };
  output_params: any;
  run_time: string;
  result: string;
}

interface TaskStatusResponse {
  execution_statuses: {
    crewAi: {
      result: {
        outputs: {
          version: string;
          config: Record<string, any>;
          requirements: any[];
          inspiration: any[];
          integrations: any[];
        };
      };
    };
  };
}

interface AppDetailResponse {
  app_id: number;
  app_name: string;
  description: string;
  app_type: string;
  status: string;
  app_json: any;
  workspace_id: number;
  creator_user_id: number;
  create_time: string;
  update_time: string;
  publish_uuid?: string;
  published_at?: string;
  app_configs: {
    configs: any[];
    result: string;
  };
}

interface AppConfig {
  api_name: string;
  result: any;
}

interface AppConfigs {
  configs: AppConfig[];
  result: string;
}

interface RunFlowRequest {
  task_input: string;
  app_id: number;
  user_id: number;
  crew_json: {
    version: string;
    config: Record<string, any>;
    requirements: any[];
    inspiration: any[];
    integrations: any[];
  };
  record_id: number;
}

interface CallbackUrlResponse {
  callback_url: string;
  status: string;
}

interface CallbackUrlModalProps {
  isOpen: boolean;
  onClose: () => void;
  publishUuid: string;
  onSuccess: () => void;
}

interface PublishConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  appName: string;
  appDescription: string;
  onPublish: (config: { name: string; description: string; icon: string }) => void;
}

const SaveVersionModal: React.FC<SaveVersionModalProps> = ({ isOpen, onClose, onSave, configs, appId }) => {
  const { t } = useTranslation();
  const timestamp = new Date().getTime();
  const [versionData, setVersionData] = useState({
    app_id: appId,
    version_name: `v1.0.${timestamp}`,
    version_code: `1.0.${timestamp}`,
    version_description: '',
    version_json: configs
  });

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>{t('apps.detail.version.title')}</h2>
        <div className="modal-form">
          <div className="form-item">
            <label htmlFor="version-name">{t('apps.detail.version.name')}:</label>
            <input
              id="version-name"
              type="text"
              value={versionData.version_name}
              onChange={(e) => setVersionData({...versionData, version_name: e.target.value})}
              placeholder={t('apps.detail.version.name')}
            />
          </div>
          <div className="form-item">
            <label htmlFor="version-code">{t('apps.detail.version.code', '版本号')}:</label>
            <input
              id="version-code"
              type="text"
              value={versionData.version_code}
              onChange={(e) => setVersionData({...versionData, version_code: e.target.value})}
              placeholder={t('apps.detail.version.code', '请输入版本号')}
            />
          </div>
          <div className="form-item">
            <label htmlFor="version-desc">{t('apps.detail.version.description')}:</label>
            <textarea
              id="version-desc"
              value={versionData.version_description}
              onChange={(e) => setVersionData({...versionData, version_description: e.target.value})}
              placeholder={t('apps.detail.version.description')}
            />
          </div>
        </div>
        <div className="modal-actions">
          <button onClick={onClose}>{t('createApp.editModal.cancel')}</button>
          <button onClick={() => onSave(versionData)} className="primary">{t('createApp.editModal.save')}</button>
        </div>
      </div>
    </div>
  );
};

const EditVersionModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  version: Version | null;
  onSave: (data: any) => void;
}> = ({ isOpen, onClose, version, onSave }) => {
  const { t } = useTranslation();
  const [editData, setEditData] = useState({
    version_name: '',
    version_code: '',
    version_description: '',
  });

  useEffect(() => {
    if (version) {
      setEditData({
        version_name: version.version_name,
        version_code: version.version_code,
        version_description: version.version_description || '',
      });
    }
  }, [version]);

  if (!isOpen || !version) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>{t('apps.detail.version.edit', '编辑版本')}</h2>
        <div className="modal-form">
          <div className="form-item">
            <label htmlFor="version-name">{t('apps.detail.version.name')}:</label>
            <input
              id="version-name"
              type="text"
              value={editData.version_name}
              onChange={(e) => setEditData({...editData, version_name: e.target.value})}
              placeholder={t('apps.detail.version.name')}
            />
          </div>
          <div className="form-item">
            <label htmlFor="version-code">{t('apps.detail.version.code', '版本号')}:</label>
            <input
              id="version-code"
              type="text"
              value={editData.version_code}
              onChange={(e) => setEditData({...editData, version_code: e.target.value})}
              placeholder={t('apps.detail.version.code', '请输入版本号')}
            />
          </div>
          <div className="form-item">
            <label htmlFor="version-desc">{t('apps.detail.version.description')}:</label>
            <textarea
              id="version-desc"
              value={editData.version_description}
              onChange={(e) => setEditData({...editData, version_description: e.target.value})}
              placeholder={t('apps.detail.version.description')}
            />
          </div>
        </div>
        <div className="modal-actions">
          <button onClick={onClose}>{t('createApp.editModal.cancel')}</button>
          <button onClick={() => onSave(editData)} className="primary">{t('createApp.editModal.save')}</button>
        </div>
      </div>
    </div>
  );
};

const RunModal: React.FC<RunModalProps & { isRunning: boolean; runProgress: RunProgress; setRunProgress: (progress: RunProgress) => void }> = ({ 
  isOpen, 
  onClose, 
  onRun, 
  versionData,
  isRunning,
  runProgress,
  setRunProgress
}) => {
  const { t } = useTranslation();
  const [taskInput, setTaskInput] = useState('');
  const [expandedSections, setExpandedSections] = useState<{
    logs: boolean;
    tasks: boolean;
    result: boolean;
  }>({
    logs: true,
    tasks: true,
    result: true
  });

  const toggleSection = (section: 'logs' | 'tasks' | 'result') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleClear = () => {
    setTaskInput('');
    setRunProgress({ logs: [], tasks: [], result: '', progress: 0 });
  };

  const handleClose = () => {
    setTaskInput('');
    setRunProgress({ logs: [], tasks: [], result: '', progress: 0 });
    onClose();
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content run-modal">
        <div className="modal-header">
          <h2>{t('apps.detail.run.modal.title')} - {versionData?.version_name}</h2>
          <div className="header-actions">
            <button className="clear-button" onClick={handleClear} disabled={isRunning}>
              {t('apps.detail.run.modal.clear', '清空')}
            </button>
            <button className="close-button" onClick={handleClose}>×</button>
          </div>
        </div>
        <div className="modal-body">
          <div className="modal-form">
            <div className="form-item">
              <label htmlFor="task-input">{t('apps.detail.run.modal.taskInput', '任务输入')}:</label>
              <div className="input-group">
                <textarea
                  id="task-input"
                  value={taskInput}
                  onChange={(e) => setTaskInput(e.target.value)}
                  placeholder={t('apps.detail.run.modal.taskInputPlaceholder', '请输入任务描述...')}
                  className="task-input"
                  disabled={isRunning}
                />
              </div>
            </div>
            {(isRunning || runProgress.logs.length > 0 || runProgress.tasks.length > 0 || runProgress.result) && (
              <div className="run-progress">
                <div className="progress-container">
                  <div className="progress-bar-container">
                    <div className="progress-bar" style={{ width: `${runProgress.progress}%` }}>
                      <span className="progress-text">{runProgress.progress}%</span>
                    </div>
                  </div>
                  
                  {runProgress.logs.length > 0 && (
                    <div className="logs-section">
                      <div className="section-header" onClick={() => toggleSection('logs')}>
                        <h4>{t('apps.detail.run.modal.executionLogs', '执行日志')}</h4>
                        <span className="collapse-icon">
                          {expandedSections.logs ? '▼' : '▶'}
                        </span>
                      </div>
                      {expandedSections.logs && (
                        <div className="logs-content">
                          {runProgress.logs.map((log, index) => (
                            <div key={index} className="log-item">{log}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {runProgress.tasks.length > 0 && (
                    <div className="tasks-section">
                      <div className="section-header" onClick={() => toggleSection('tasks')}>
                        <h4>{t('apps.detail.run.modal.taskExecution', '任务执行情况')}</h4>
                        <span className="collapse-icon">
                          {expandedSections.tasks ? '▼' : '▶'}
                        </span>
                      </div>
                      {expandedSections.tasks && (
                        <div className="tasks-preview">
                          <table className="timeline-table">
                            <thead>
                              <tr>
                                <th>{t('apps.detail.run.modal.time', '时间')}</th>
                                <th>{t('apps.detail.record.status')}</th>
                                <th>{t('apps.detail.run.modal.content', '内容')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {runProgress.tasks.map((task, index) => (
                                <tr key={index} className="timeline-item">
                                  <td className="timeline-time">{formatTimestamp(task.timestamp)}</td>
                                  <td className="timeline-status">
                                    <span className={`status-badge ${task.status}`}>
                                      {task.status === 'started' ? t('apps.detail.record.statusStarted', '执行中') : t('apps.detail.version.statusCompleted', '已完成')}
                                    </span>
                                  </td>
                                  <td className="timeline-content">
                                    <div className="task-details">
                                      <div className="task-header">
                                        <span className="task-name">{task.task_name}</span>
                                        <span className="task-agent">{task.agent}</span>
                                      </div>
                                      {task.content && (
                                        <div className="task-content">
                                          {/* 使用 Markdown 组件渲染内容，支持表格和 emoji */}
                                          <Markdown content={task.content} className="markdown-body" />
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {runProgress.result && (
                    <div className="run-result">
                      <div className="section-header" onClick={() => toggleSection('result')}>
                        <h4>{t('apps.detail.run.modal.runResult', '运行结果')}</h4>
                        <span className="collapse-icon">
                          {expandedSections.result ? '▼' : '▶'}
                        </span>
                      </div>
                      {expandedSections.result && (
                        <div className="result-content">
                          {/* 使用 Markdown 组件渲染内容，支持表格和 emoji */}
                          <Markdown content={runProgress.result} className="markdown-body" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button 
            className="run-button" 
            onClick={() => onRun(taskInput)} 
            disabled={isRunning || !taskInput.trim()}
          >
            {isRunning ? t('apps.detail.run.modal.stop') : t('apps.detail.run.modal.start')}
          </button>
        </div>
      </div>
    </div>
  );
};

const RecordDetailModal: React.FC<RecordDetailModalProps> = ({ isOpen, onClose, record }) => {
  const { t } = useTranslation();
  
  if (!isOpen || !record) return null;

  const formatJson = (json: any) => {
    try {
      return JSON.stringify(json, null, 2);
    } catch (e) {
      return '无法格式化JSON数据';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const renderMarkdown = (content: string) => {
    if (!content) return '';
    
    // 处理粗体
    content = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // 处理斜体
    content = content.replace(/\*(.*?)\*/g, '<em>$1</em>');
    // 处理标题
    content = content.replace(/^# (.*$)/gm, '<h1 class="markdown-h1">$1</h1>');
    content = content.replace(/^## (.*$)/gm, '<h2 class="markdown-h2">$1</h2>');
    content = content.replace(/^### (.*$)/gm, '<h3 class="markdown-h3">$1</h3>');
    content = content.replace(/^#### (.*$)/gm, '<h4 class="markdown-h4">$1</h4>');
    // 处理列表
    content = content.replace(/^\s*[-*] (.*$)/gm, '<li class="markdown-li">$1</li>');
    // 处理分割线
    content = content.replace(/^---$/gm, '<hr class="markdown-hr" />');
    
    // 改进的表格处理
    const tableRegex = /(\|[^\n]+\|\r?\n)((?:\|:?-+:?)+\|)(\r?\n(?:\|[^\n]+\|\r?\n?)*)/g;
    content = content.replace(tableRegex, (match, header, separator, body) => {
      try {
        // 处理表头
        const headerCells = header.split('|').filter((cell: string) => cell.trim() !== '').map((cell: string) => cell.trim());
        const headerRow = `<thead><tr>${headerCells.map((cell: string) => `<th>${cell}</th>`).join('')}</tr></thead>`;
        
        // 处理表格主体
        const bodyRows = body.trim().split('\n').map((row: string) => {
          const cells = row.split('|').filter((cell: string) => cell.trim() !== '').map((cell: string) => cell.trim());
          return `<tr>${cells.map((cell: string) => `<td>${cell}</td>`).join('')}</tr>`;
        }).join('');
        
        return `<table class="markdown-table"><colgroup>${headerCells.map((): string => '<col />').join('')}</colgroup>${headerRow}<tbody>${bodyRows}</tbody></table>`;
      } catch (e) {
        console.error("表格解析错误:", e);
        return match; // 如果解析出错，返回原始 markdown
      }
    });
    
    // 处理段落
    content = content.split('\n\n').map((paragraph: string) => {
      if (!paragraph.startsWith('<') || !paragraph.endsWith('>')) {
        return `<p class="markdown-p">${paragraph}</p>`;
      }
      return paragraph;
    }).join('\n\n');

    return <div className="markdown-container" dangerouslySetInnerHTML={{ __html: content }} />;
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content record-detail-modal">
        <div className="modal-header">
          <h2>{t('apps.detail.record.detail', '运行记录详情')} - ID: {record.record_id}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="record-detail-content">
          <div className="detail-section">
            <h3>{t('apps.detail.version.basicInfo', '基本信息')}</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>{t('apps.detail.record.appId', '应用ID')}:</label>
                <span>{record.app_id}</span>
              </div>
              <div className="info-item">
                <label>{t('apps.detail.record.userId', '用户ID')}:</label>
                <span>{record.user_id}</span>
              </div>
              <div className="info-item">
                <label>{t('apps.detail.record.time')}:</label>
                <span>{formatTimestamp(record.run_time)}</span>
              </div>
              <div className="info-item">
                <label>{t('apps.detail.record.status')}:</label>
                <span className={`status-badge ${record.result}`}>
                  {record.result === 'success' ? t('apps.detail.record.statusSuccess', '成功') : 
                   record.result === 'error' ? t('apps.detail.record.statusError', '失败') :
                   record.result === 'started' ? t('apps.detail.record.statusStarted', '执行中') : record.result}
                </span>
              </div>
            </div>
          </div>
          
          <div className="detail-section">
            <h3>{t('apps.detail.record.inputParams', '输入参数')}</h3>
            <div className="input-params">
              <div className="param-group">
                <h4>{t('apps.detail.run.modal.taskInput', '任务输入')}</h4>
                <div className="param-content">
                  {record.output_params?.input?.task_input || t('apps.detail.record.noInputData', '无输入数据')}
                </div>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>{t('apps.detail.run.modal.runResult', '运行结果')}</h3>
            <div className="result-content">
              {record.output_params?.final_result?.content ? (
                <Markdown content={record.output_params.final_result.content} className="markdown-body" />
              ) : (
                <p>{t('apps.detail.record.noRunResult', '无运行结果')}</p>
              )}
            </div>
          </div>

          <div className="detail-section">
            <h3>{t('apps.detail.record.executionProcess', '执行过程')}</h3>
            <div className="execution-timeline">
              <table className="timeline-table">
                <thead>
                  <tr>
                    <th>{t('apps.detail.run.modal.time', '时间')}</th>
                    <th>{t('apps.detail.record.status')}</th>
                    <th>{t('apps.detail.run.modal.content', '内容')}</th>
                  </tr>
                </thead>
                <tbody>
                  {record.output_params?.intermediate_results?.map((result: any, index: number) => {
                    // 如果是执行中状态，不显示中间过程
                    if (record.result === 'started') {
                      return null;
                    }
                    return (
                      <tr key={index} className={`timeline-item ${result.type}`}>
                        <td className="timeline-time">{formatTimestamp(result.timestamp)}</td>
                        <td className="timeline-status">
                          {result.type === 'task' ? (
                            <span className={`status-badge ${result.status}`}>
                              {result.status === 'started' ? t('apps.detail.record.statusStarted', '执行中') : t('apps.detail.version.statusCompleted', '已完成')}
                            </span>
                          ) : (
                            <span className="status-badge log">{t('apps.detail.record.statusLog', '日志')}</span>
                          )}
                        </td>
                        <td className="timeline-content">
                          {result.type === 'task' ? (
                            <div className="task-details">
                              <div className="task-header">
                                <span className="task-name">{result.task_name}</span>
                                <span className="task-agent">{result.agent}</span>
                              </div>
                              {result.output && (
                                <div className="task-output">
                                  {/* 使用 Markdown 组件渲染内容，支持表格和 emoji */}
                                  <Markdown content={result.output} className="markdown-body" />
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="log-content">{result.content}</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {record.result === 'started' && (
                <div className="execution-in-progress">
                  <p>{t('apps.detail.record.taskRunning', '任务正在执行中，请稍后查看执行过程...')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const VersionDetailModal: React.FC<VersionDetailModalProps> = ({ isOpen, onClose, version }) => {
  const { t } = useTranslation();
  console.log('VersionDetailModal render:', { isOpen, version });
  
  if (!isOpen || !version) {
    console.log('VersionDetailModal early return:', { isOpen, version });
    return null;
  }

  // 添加版本数据的验证
  const validateVersionData = (data: any) => {
    console.log('Validating version data:', data);
    if (!data) return false;
    if (!data.version_json) return false;
    return true;
  };

  if (!validateVersionData(version)) {
    console.error('Invalid version data:', version);
    return (
      <div className="modal-overlay">
        <div className="modal-content version-detail-modal">
          <h2>{t('apps.detail.version.title')}</h2>
          <div className="error-message">
            <p>{t('apps.detail.version.invalidData', '版本数据无效或格式不正确')}</p>
            <pre>{JSON.stringify(version, null, 2)}</pre>
          </div>
          <div className="modal-actions">
            <button onClick={onClose}>{t('apps.detail.run.modal.close')}</button>
          </div>
        </div>
      </div>
    );
  }

  // 解析 parsed_workflow 数据
  const parsedWorkflows = version.version_json?.parsed_workflow || [];

  return (
    <div className="modal-overlay">
      <div className="modal-content version-detail-modal">
        <h2>{t('apps.detail.version.title')}</h2>
        <div className="version-detail-content">
          <div className="detail-section">
            <h3>{t('apps.detail.version.basicInfo', '基本信息')}</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>{t('apps.detail.version.name')}:</label>
                <span>{version.version_name}</span>
              </div>
              <div className="info-item">
                <label>{t('apps.detail.version.code', '版本号')}:</label>
                <span>{version.version_code}</span>
              </div>
              <div className="info-item">
                <label>{t('apps.detail.version.status')}:</label>
                <span className={`status-badge ${version.version_status}`}>
                  {version.version_status}
                </span>
              </div>
              {/* 删除创建时间 */}
            </div>
          </div>

          <div className="detail-section">
            <h3>{t('apps.detail.version.description')}</h3>
            <p className="version-description">
              {version.version_description || t('apps.detail.version.noDescription', '暂无描述')}
            </p>
          </div>

          <div className="detail-section">
            <h3>{t('apps.detail.version.configInfo', '配置信息')}</h3>
            <div className="version-json">
              <div className="json-section">
                <h4>{t('apps.detail.version.basicInfo', '基本信息')}</h4>
                <p>{t('apps.detail.version.name')}: {version.version_json?.name || '-'}</p>
                <p>{t('apps.detail.version.type', '类型')}: {version.version_json?.type || '-'}</p>
                <p>{t('apps.detail.version.language', '语言')}: {version.version_json?.language || '-'}</p>
              </div>
              
              {parsedWorkflows && parsedWorkflows.length > 0 && (
                <div className="json-section">
                  <h4>{t('apps.detail.version.workflowDetails', '工作流详情')}</h4>
                  {parsedWorkflows.map((workflow: any, index: number) => (
                    <div key={index} className="workflow-item">
                      <h5>{workflow.work_name || t('apps.detail.version.workflow', '工作流') + ` ${index + 1}`}</h5>
                      <p><strong>{t('apps.detail.version.role', '角色')}:</strong> {workflow.agent_role || '-'}</p>
                      <p><strong>{t('apps.detail.version.goal', '目标')}:</strong> {workflow.agent_goal || '-'}</p>
                      <p><strong>{t('apps.detail.version.background', '背景')}:</strong> {workflow.agent_backstory || '-'}</p>
                      <div className="task-section">
                        <p><strong>{t('apps.detail.version.taskDescription', '任务描述')}:</strong></p>
                        <div className="code-block">
                          <pre>{workflow.task_description || '-'}</pre>
                        </div>
                      </div>
                      <div className="task-section">
                        <p><strong>{t('apps.detail.version.expectedOutput', '预期输出')}:</strong></p>
                        <div className="code-block">
                          <pre>{workflow.task_expected_output || '-'}</pre>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {version.version_json?.works && Array.isArray(version.version_json.works) && version.version_json.works.length > 0 && (
                <div className="json-section">
                  <h4>{t('apps.detail.version.workItems', '工作项')}</h4>
                  {version.version_json.works.map((work, index) => (
                    <div key={index} className="work-item">
                      <p><strong>{work.name}:</strong> {work.description}</p>
                    </div>
                  ))}
                </div>
              )}
              {version.version_json?.configs?.agents && Array.isArray(version.version_json.configs.agents) && version.version_json.configs.agents.length > 0 && (
                <div className="json-section">
                  <h4>{t('apps.detail.version.agentConfig', '智能体配置')}</h4>
                  {version.version_json.configs.agents.map((agent, index) => (
                    <div key={index} className="code-block">
                      <pre>{agent}</pre>
                    </div>
                  ))}
                </div>
              )}
              {version.version_json?.configs?.tasks && Array.isArray(version.version_json.configs.tasks) && version.version_json.configs.tasks.length > 0 && (
                <div className="json-section">
                  <h4>{t('apps.detail.version.taskConfig', '任务配置')}</h4>
                  {version.version_json.configs.tasks.map((task, index) => (
                    <div key={index} className="code-block">
                      <pre>{task}</pre>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-actions">
          <button onClick={onClose}>{t('apps.detail.run.modal.close')}</button>
        </div>
      </div>
    </div>
  );
};

// API文档已移至单独的页面 ApiDocsPage.tsx

const CallbackUrlModal: React.FC<CallbackUrlModalProps> = ({ isOpen, onClose, publishUuid, onSuccess }) => {
  const [callbackUrl, setCallbackUrl] = useState('https://www.iagent.cc/auth-api/billing-callback');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen && publishUuid) {
      fetchCallbackUrl();
    }
  }, [isOpen, publishUuid]);

  const fetchCallbackUrl = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get<CallbackUrlResponse>(
        `https://ceshi.lightark.cc/api/v1/billing/callback-url/${publishUuid}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (response.data?.callback_url) {
        setCallbackUrl(response.data.callback_url);
      }
    } catch (error) {
      console.error('获取回调地址失败:', error);
    }
  };

  const handleSave = async () => {
    if (!callbackUrl) {
      setError('请输入回调地址');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const url = `https://ceshi.lightark.cc/api/v1/billing/callback-url`;
      const data = {
        publish_uuid: publishUuid,
        callback_url: callbackUrl,
        status: 'active'
      };

      await axios.post(url, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      notification.success({
        message: t('apps.detail.callbackUrl.success', '保存成功'),
        description: t('apps.detail.callbackUrl.successDescription', '回调地址配置成功'),
        duration: 3
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('保存回调地址失败:', error);
      setError('保存回调地址失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t('apps.detail.callbackUrl.title', '配置回调地址')}
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('apps.detail.callbackUrl.cancel', '取消')}
        </Button>,
        <Button 
          key="save" 
          type="primary" 
          onClick={handleSave}
          loading={loading}
        >
          {t('apps.detail.callbackUrl.save', '保存')}
        </Button>
      ]}
    >
      <Form layout="vertical">
        <Form.Item
          label={t('apps.detail.callbackUrl.urlLabel', '回调地址')}
          validateStatus={error ? 'error' : ''}
          help={error}
        >
          <Input
            value={callbackUrl}
            onChange={(e) => setCallbackUrl(e.target.value)}
            placeholder={t('apps.detail.callbackUrl.urlPlaceholder', '请输入回调地址')}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const PublishConfigModal: React.FC<PublishConfigModalProps> = ({ isOpen, onClose, appName, appDescription, onPublish }) => {
  const [name, setName] = useState(appName);
  const [description, setDescription] = useState(appDescription);
  const [icon, setIcon] = useState('');
  const [icons, setIcons] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      // fetchIcons();
    }
  }, [isOpen]);

  const fetchIcons = async () => {
    try {
      const response = await fetch('https://www.iagent.cc/images/icon/');
      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const links = Array.from(doc.querySelectorAll('a'));
      const iconList = links
        .map(link => link.textContent?.trim())
        .filter(name => name && name.endsWith('.svg'))
        .map(name => name?.replace('.svg', '')) as string[];
      setIcons(iconList);
    } catch (error) {
      console.error('获取图标列表失败:', error);
    }
  };

  const handlePublish = async () => {
    if (!name || !description) {
      message.error('请填写完整信息');
      return;
    }

    setLoading(true);
    try {
      onPublish({ name, description, icon });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t('apps.detail.publishToIAgent.title', '发布到iAgent')}
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('apps.detail.publishToIAgent.cancel', '取消')}
        </Button>,
        <Button 
          key="publish" 
          type="primary" 
          onClick={handlePublish}
          loading={loading}
        >
          {t('apps.detail.publishToIAgent.publish', '发布')}
        </Button>
      ]}
    >
      <Form layout="vertical">
        <Form.Item
          label={t('apps.detail.publishToIAgent.name', 'Agent名称')}
          required
        >
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('apps.detail.publishToIAgent.namePlaceholder', '请输入Agent名称')}
          />
        </Form.Item>
        <Form.Item
          label={t('apps.detail.publishToIAgent.description', '描述信息')}
          required
        >
          <Input.TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('apps.detail.publishToIAgent.descriptionPlaceholder', '请输入描述信息')}
            rows={4}
          />
        </Form.Item>
        {/* <Form.Item
          label={t('apps.detail.publishToIAgent.icon', '图标')}
          required
        >
          <Select
            value={icon}
            onChange={setIcon}
            placeholder={t('apps.detail.publishToIAgent.iconPlaceholder', '请选择图标')}
            style={{ width: '100%' }}
          >
            {icons.map((iconName) => (
              <Select.Option key={iconName} value={iconName}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <img 
                    src={`https://www.iagent.cc/images/icon/${iconName}.svg`} 
                    alt={iconName}
                    style={{ width: 24, height: 24, marginRight: 8 }}
                  />
                  {iconName}
                </div>
              </Select.Option>
            ))}
          </Select>
        </Form.Item> */}
      </Form>
    </Modal>
  );
};

const AppDetail: React.FC = () => {
  const { t } = useTranslation();
  const { appId } = useParams<{ appId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const workspaceId = searchParams.get('workspace_id');
  
  const [appData, setAppData] = useState<AppData | null>(null);
  const [versions, setVersions] = useState<Version[]>([]);
  const [runRecords, setRunRecords] = useState<RunRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('scene');
  const [sceneDescription, setSceneDescription] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  
  // 下拉菜单的引用
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // 添加点击外部关闭下拉菜单的功能
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownVisible(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [agentOutput, setAgentOutput] = useState<AgentOutput | null>({
    name: '',
    type: '',
    configs: {
      agents: [],
      tasks: []
    },
    language: '',
    works: []
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRunModalOpen, setIsRunModalOpen] = useState(false);
  const [selectedVersionData, setSelectedVersionData] = useState<Version | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<RunRecord | null>(null);
  const [isRecordDetailOpen, setIsRecordDetailOpen] = useState(false);
  const [isVersionDetailOpen, setIsVersionDetailOpen] = useState(false);
  const [selectedVersionDetail, setSelectedVersionDetail] = useState<VersionDetail | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [apiResponse, setApiResponse] = useState<any>(null);
  const [runProgress, setRunProgress] = useState<RunProgress>({
    logs: [],
    tasks: [],
    result: '',
    progress: 0
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<{
    logs: boolean;
    tasks: boolean;
    result: boolean;
  }>({
    logs: true,
    tasks: true,
    result: true
  });
  const [taskInput, setTaskInput] = useState('');
  const [selectedVersionForRun, setSelectedVersionForRun] = useState<Version | null>(null);
  const [isRunSheetOpen, setIsRunSheetOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<TabType>('scene');
  const [loadingPreviewIds, setLoadingPreviewIds] = useState<number[]>([]);
  const [isCallbackUrlModalOpen, setIsCallbackUrlModalOpen] = useState(false);
  const [hasCallbackUrl, setHasCallbackUrl] = useState(false);
  const [isPublishConfigModalOpen, setIsPublishConfigModalOpen] = useState(false);
  
  // API文档已移至单独的页面
  
  const toggleSection = (section: 'logs' | 'tasks' | 'result') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const fetchAppDetail = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get<AppDetailResponse>(
        `https://ceshi.lightark.cc/api/v1/apps/${appId}/detail`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            workspace_id: workspaceId
          }
        }
      );

      console.log('获取应用详情响应:', response.data);

      // 使用函数参数形式的setState，避免依赖于当前的appData状态
      setAppData((prevAppData) => {
        // 检查当前app_description是否为空
        const isDescriptionEmpty = !prevAppData?.app_description || prevAppData.app_description.trim() === '';
        
        // 转换响应数据以匹配 AppData 结构
        const newAppData: AppData = {
          ...response.data,
          app_description: isDescriptionEmpty ? response.data.description : prevAppData?.app_description || response.data.description
        };

        console.log('应用描述是否为空:', isDescriptionEmpty);
        console.log('使用的应用描述:', newAppData.app_description);

        return newAppData;
      });
    } catch (error) {
      console.error('获取应用详情失败:', error);
      // 使用notification代替setError
      notification.error({
        message: '获取应用详情失败',
        description: '获取应用详情时发生错误，请稍后重试。',
        duration: 3
      });
    }
  }, [appId, workspaceId, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!workspaceId) {
        notification.error({
          message: '缺少参数',
          description: '缺少工作空间ID，请检查URL参数。',
          duration: 3
        });
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const headers = { Authorization: `Bearer ${token}` };
        setLoading(true);

        try {
          const [appResponse, versionsResponse, recordsResponse] = await Promise.all([
            axios.get<AppData>(`https://ceshi.lightark.cc/api/v1/apps/${appId}`, {
              headers,
              params: { workspace_id: workspaceId }
            }),
            axios.get<Version[]>(`https://ceshi.lightark.cc/api/v1/versions/`, {
              headers,
              params: { 
                app_id: appId,
                workspace_id: workspaceId,
                skip: 0,
                limit: 50
              }
            }),
            axios.get<RunRecord[]>(`https://ceshi.lightark.cc/api/v1/run-records/`, {
              headers,
              params: { 
                app_id: appId,
                workspace_id: workspaceId,
                skip: 0,
                limit: 50
              }
            })
          ]);

          setAppData(appResponse.data);
          setVersions(versionsResponse.data);
          setRunRecords(recordsResponse.data);
          if (versionsResponse.data.length > 0) {
            setSelectedVersion(versionsResponse.data[0].version_id);
          }
        } catch (error) {
          const err = error as ApiError;
          if (err.response?.status === 401) {
            navigate('/login');
            return;
          }
          
          // 使用notification代替setError
          notification.error({
            message: '数据加载失败',
            description: err.response?.data?.message || '获取数据失败，请刷新页面重试。',
            duration: 3
          });
          console.error('Error fetching data:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [appId, workspaceId, navigate]);

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    window.location.reload();
  };

  const handleGenerateAgent = async () => {
    try {
      setIsGenerating(true);
      setError(null);
      const response = await axios.post<WorkflowResponse>('https://www.kofe.ai/v1/workflows/run', {
        inputs: {
          ia_description: sceneDescription
        },
        response_mode: "blocking",
        user: "jingluke"
      }, {
        headers: {
          'Authorization': 'Bearer app-Dtv9hH9LKPusmSHY6BrSDEn4',
          'Content-Type': 'application/json'
        }
      });

      setApiResponse(response.data);

      if (response.data.data?.outputs) {
        setAgentOutput(response.data.data.outputs);
      } else {
        setError('生成结果不符合预期，请重新生成');
        console.log('API返回数据:', JSON.stringify(response.data, null, 2));
      }
    } catch (err: any) {
      const error = err as Error & { response?: any };
      console.error('生成智能体失败:', error);
      setError('生成智能体失败，请重新尝试');
      if (error.response) {
        console.log('API错误响应:', JSON.stringify(error.response.data, null, 2));
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveVersion = async (versionData: any) => {
    try {
      const token = localStorage.getItem('token');
      
      const dataToSave = {
        ...versionData,
        version_json: agentOutput,
        workspace_id: workspaceId
      };

      await axios.post('https://ceshi.lightark.cc/api/v1/versions/', dataToSave, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      message.success('版本保存成功');
      window.location.reload();
    } catch (error: any) {
      console.error('保存版本失败:', error);
      // 使用notification代替setError
      notification.error({
        message: '保存版本失败',
        description: error.response?.data?.message || '保存版本时发生错误，请稍后重试。',
        duration: 3
      });
      if (error.response) {
        console.log('保存版本错误响应:', JSON.stringify(error.response.data, null, 2));
      }
    }
  };

  const processJsonData = (jsonStr: string): WorkResponse | null => {
    try {
      // 处理不完整的 JSON
      if (!jsonStr.endsWith('}')) {
        jsonStr = jsonStr + '}';
      }

      // 处理中文字符
      jsonStr = jsonStr.replace(/[\u0000-\u0019]+/g, ''); // 移除控制字符
      jsonStr = jsonStr.replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029'); // 处理特殊换行符

      console.log('Processed JSON:', jsonStr);
      const data = JSON.parse(jsonStr) as WorkResponse;
      
      // 验证 work 类型数据的完整性
      if (data.type === 'work') {
        const workData = data as WorkProgress;
        if (!workData.timestamp || !workData.task_name || !workData.task || !workData.agent || !workData.status) {
          return null;
        }
      }
      
      return data;
    } catch (e) {
      console.error('JSON parse error:', e);
      console.error('Problematic JSON:', jsonStr);
      return null;
    }
  };

  const fetchRunRecords = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get<RunRecord[]>(`https://ceshi.lightark.cc/api/v1/run-records/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: { 
          app_id: appId,
          workspace_id: workspaceId,
          skip: 0,
          limit: 50
        }
      });
      setRunRecords(response.data);
    } catch (error) {
      console.error('获取运行记录失败:', error);
    }
  }, [appId, workspaceId, navigate]);

  useEffect(() => {
    fetchRunRecords();
  }, [fetchRunRecords]);

  useEffect(() => {
    if (!isRunning && runProgress.progress === 100) {
      fetchRunRecords();
    }
  }, [isRunning, runProgress.progress, fetchRunRecords]);

  const handleRunVersion = async (taskInput: string) => {
    try {
      setIsRunning(true);
      setRunProgress({ logs: [], tasks: [], result: '', progress: 0 });
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('user_id');
      
      if (!token || !userId) {
        navigate('/login');
        return;
      }

      if (!appId) {
        notification.error({
          message: '缺少参数',
          description: '缺少应用ID，请检查URL参数。',
          duration: 3
        });
        return;
      }

      console.log('开始获取应用详情...');
      // 获取应用详情
      const appResponse = await axios.get<AppDetailResponse>(`https://ceshi.lightark.cc/api/v1/apps/${appId}/detail`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        params: {
          workspace_id: workspaceId
        }
      });

      console.log('应用详情响应:', appResponse.data);

      // 从app_configs中获取crewAi的内容
      const appConfigs = appResponse.data.app_configs;
      console.log('app_configs:', JSON.stringify(appConfigs, null, 2));

      let crewJson = {
        version: "1.0.0",
        config: {},
        requirements: [],
        inspiration: [],
        integrations: []
      };

      // 查找crewAi的配置
      if (appConfigs && Array.isArray(appConfigs)) {
        console.log('开始查找crewAi配置...');
        const crewAiConfig = appConfigs.find(config => {
          console.log('检查配置:', config.api_name);
          return config.api_name === 'crewAi';
        });
        
        console.log('找到的crewAi配置:', JSON.stringify(crewAiConfig, null, 2));
        
        if (crewAiConfig && crewAiConfig.result) {
          console.log('crewAi配置的result字段:', JSON.stringify(crewAiConfig.result, null, 2));
          crewJson = crewAiConfig.result;
          console.log('最终使用的crewJson:', JSON.stringify(crewJson, null, 2));
        } else {
          console.warn('未找到有效的crewAi配置或result字段为空');
        }
      } else {
        console.warn('app_configs不是数组或为空');
      }

      // 创建运行记录
      const runRecordResponse = await axios.post<RunRecordResponse>(
        'https://ceshi.lightark.cc/api/v1/run-records/',
        {
          app_id: parseInt(appId),
          user_id: parseInt(userId),
          version_id: 0,
          input_params: {
            task_input: taskInput,
            crew_json: crewJson
          },
          output_params: {},
          result: 'started'
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      const recordId = runRecordResponse.data.record_id;
      console.log('创建的运行记录ID:', recordId);

      // 准备运行流程请求数据
      const runFlowRequest: RunFlowRequest = {
        task_input: taskInput,
        app_id: parseInt(appId),
        user_id: parseInt(userId),
        crew_json: crewJson,
        record_id: recordId
      };

      console.log('发送的运行流程请求:', JSON.stringify(runFlowRequest, null, 2));

      // 开始运行流程
      const flowResponse = await fetch('https://ceshi.lightark.cc/api/v1/flow/run-flow', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(runFlowRequest)
      });

      if (!flowResponse.ok) {
        throw new Error(`HTTP error! status: ${flowResponse.status}`);
      }

      const reader = flowResponse.body?.getReader();
      if (!reader) {
        throw new Error('No reader available');
      }

      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          setRunProgress(prev => ({
            ...prev,
            progress: 100
          }));
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6).trim();
            if (!jsonStr) continue;

            const data = processJsonData(jsonStr);
            if (!data) continue;

            if (data.type === 'log') {
              setRunProgress(prev => ({
                ...prev,
                logs: [...prev.logs, data.content],
                progress: Math.min(90, prev.progress + 15)
              }));
            } else if (data.type === 'work') {
              const workData = data as WorkProgress;
              setRunProgress(prev => ({
                ...prev,
                tasks: [...prev.tasks, {
                  timestamp: workData.timestamp,
                  task_name: workData.task_name,
                  task: workData.task,
                  agent: workData.agent,
                  status: workData.status,
                  content: workData.content
                }],
                progress: Math.min(90, prev.progress + 15)
              }));
            } else if (data.type === 'result') {
              const resultData = data as WorkResult;
              setRunProgress(prev => ({
                ...prev,
                result: resultData.content,
                progress: 100
              }));
            }
          }
        }
      }
    } catch (error: unknown) {
      console.error('运行版本错误:', error);
      const apiError = error as ApiErrorResponse;
      if (apiError.response?.status === 401) {
        navigate('/login');
        return;
      }
      // 使用notification代替setError
      notification.error({
        message: '运行失败',
        description: apiError.response?.data?.message || '运行过程中发生错误，请稍后重试。',
        duration: 3
      });
    } finally {
      setIsRunning(false);
    }
  };

  const handleViewVersionDetail = async (versionId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get<VersionDetail>(`https://ceshi.lightark.cc/api/v1/versions/${versionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        params: {
          workspace_id: workspaceId
        }
      });
      setSelectedVersionDetail(response.data);
      setIsVersionDetailOpen(true);
    } catch (error) {
      console.error('获取版本详情失败:', error);
      // 使用notification代替setError
      notification.error({
        message: '获取版本详情失败',
        description: '获取版本详情时发生错误，请稍后重试。',
        duration: 3
      });
    }
  };

  const handleSaveEdit = async (editData: any) => {
    try {
      if (!selectedVersionData) return;
      
      const token = localStorage.getItem('token');
      await axios.put(
        `https://ceshi.lightark.cc/api/v1/versions/${selectedVersionData.version_id}`,
        {
          ...editData,
          workspace_id: workspaceId
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        }
      );
      
      // 重新加载版本列表
      const versionsResponse = await axios.get<Version[]>(`https://ceshi.lightark.cc/api/v1/versions/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: { 
          app_id: appId,
          workspace_id: workspaceId,
          skip: 0,
          limit: 50
        }
      });
      setVersions(versionsResponse.data);
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('更新版本失败:', error);
    }
  };

  const handleDeleteRecord = async (recordId: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      await axios.delete(`https://ceshi.lightark.cc/api/v1/run-records/${recordId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        params: {
          workspace_id: workspaceId
        }
      });

      message.success('删除记录成功');

      // 重新加载运行记录
      const response = await axios.get<RunRecord[]>(`https://ceshi.lightark.cc/api/v1/run-records/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        params: { 
          app_id: appId,
          workspace_id: workspaceId,
          skip: 0,
          limit: 50
        }
      });
      setRunRecords(response.data);
    } catch (error) {
      console.error('删除运行记录失败:', error);
      // 使用notification代替setError
      notification.error({
        message: '删除运行记录失败',
        description: '删除运行记录时发生错误，请稍后重试。',
        duration: 3
      });
    }
  };

  const renderVersionCard = (version: Version) => {
    const handleDelete = async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (window.confirm('确定要删除此版本吗？')) {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`https://ceshi.lightark.cc/api/v1/versions/${version.version_id}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            params: {
              workspace_id: workspaceId
            }
          });
          
          message.success('删除版本成功');
          // 重新加载版本列表
          const versionsResponse = await axios.get<Version[]>(`https://ceshi.lightark.cc/api/v1/versions/`, {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            params: { 
              app_id: appId,
              workspace_id: workspaceId,
              skip: 0,
              limit: 50
            }
          });
          setVersions(versionsResponse.data);
        } catch (error) {
          console.error('删除版本失败:', error);
          // 使用notification代替弹出错误页面
          notification.error({
            message: '删除版本失败',
            description: '删除版本时发生错误，请稍后重试。',
            duration: 3
          });
        }
      }
    };

    const handleEdit = (e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedVersionData(version);
      setIsEditModalOpen(true);
    };

    // 获取 parsed_workflow 中的第一个工作流（如果存在）
    const firstWorkflow = version.version_json?.parsed_workflow && 
                         Array.isArray(version.version_json.parsed_workflow) && 
                         version.version_json.parsed_workflow.length > 0 ? 
                         version.version_json.parsed_workflow[0] : null;

    return (
      <div 
        key={version.version_id}
        className={`version-card ${selectedVersion === version.version_id ? 'selected' : ''}`}
      >
        <div className="version-header">
          <h3 onClick={() => handleViewVersionDetail(version.version_id)}>{version.version_name}</h3>
          <div className="version-actions">
            <button 
              className="action-button edit-button"
              onClick={handleEdit}
            >
              编辑
            </button>
            <button 
              className="action-button delete-button"
              onClick={handleDelete}
            >
              删除
            </button>
            <button 
              className="action-button run-button"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedVersionForRun(version);
                setActiveTab('scene');
                setIsRunSheetOpen(true);
              }}
            >
              运行
            </button>
          </div>
        </div>
        <div onClick={() => handleViewVersionDetail(version.version_id)}>
          <p>{version.version_description}</p>
          <div className="version-info">
            <span>版本号: {version.version_code}</span>
            <span className={`status-badge ${version.version_status}`}>
              {version.version_status}
            </span>
          </div>
          {/* 删除创建时间 */}
          {version.version_json && (
            <div className="version-json-preview">
              <h4>配置信息</h4>
              <div className="json-section">
                <p><strong>名称:</strong> {version.version_json.name}</p>
                <p><strong>类型:</strong> {version.version_json.type}</p>
                <p><strong>语言:</strong> {version.version_json.language}</p>
              </div>
              
              {firstWorkflow && (
                <div className="json-section">
                  <h5>角色信息</h5>
                  <div className="role-item">
                    <p><strong>角色:</strong> {firstWorkflow.agent_role || '-'}</p>
                    <p><strong>目标:</strong> {firstWorkflow.agent_goal || '-'}</p>
                    {firstWorkflow.agent_backstory && (
                      <p><strong>背景:</strong> {firstWorkflow.agent_backstory.length > 100 ? 
                                                `${firstWorkflow.agent_backstory.substring(0, 100)}...` : 
                                                firstWorkflow.agent_backstory}</p>
                    )}
                  </div>
                </div>
              )}
              
              {version.version_json.works && version.version_json.works.length > 0 && (
                <div className="json-section">
                  <h5>工作项</h5>
                  {version.version_json.works.map((work, index) => (
                    <div key={index} className="work-item">
                      <p><strong>{work.name}:</strong> {work.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // 处理版本选择
  const handleVersionSelect = (version: Version) => {
    setSelectedVersionForRun(version);
    setActiveTab('scene');
  };

  // 处理运行页面关闭
  const handleRunSheetClose = () => {
    setSelectedVersionForRun(null);
    setTaskInput('');
    setRunProgress({ logs: [], tasks: [], result: '', progress: 0 });
  };

  /**
   * 计算相对时间
   * @param dateString 时间字符串
   * @returns 相对时间字符串
   */
  const getRelativeTime = (dateString: string) => {
    if (!dateString) return '';
    
    // 使用try-catch块捕获可能的错误
    try {
      const now = new Date();
      const date = new Date(dateString);
      const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
      
      // 不到1分钟
      if (diffInSeconds < 60) {
        return t('apps.time.justNow');
      }
      
      // 不到1小时
      if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return t('apps.time.minutesAgo', { minutes });
      }
      
      // 不到1天
      if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return t('apps.time.hoursAgo', { hours });
      }
      
      // 不到1个月
      if (diffInSeconds < 2592000) {
        const days = Math.floor(diffInSeconds / 86400);
        return t('apps.time.daysAgo', { days });
      }
      
      // 不到1年
      if (diffInSeconds < 31536000) {
        const months = Math.floor(diffInSeconds / 2592000);
        return t('apps.time.monthsAgo', { months });
      }
      
      // 超过1年
      const years = Math.floor(diffInSeconds / 31536000);
      return t('apps.time.yearsAgo', { years });
    } catch (error) {
      console.error('计算相对时间出错:', error);
      // 出错时返回一个安全的默认值
      return dateString;
    }
  };
  
  /**
   * 发布应用
   */
  const handlePublishApp = async () => {
    if (!appId) return;
    
    setIsPublishing(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      // 调用发布API
      await axios.post(
        'https://ceshi.lightark.cc/api/v1/apps/publish',
        { app_id: parseInt(appId) },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // 重新获取应用详情
      await fetchAppDetail();
      
      notification.success({
        message: t('apps.detail.publish.success', '发布成功'),
        description: t('apps.detail.publish.successDescription', '应用已成功发布'),
        duration: 3
      });
    } catch (error) {
      console.error('发布应用失败:', error);
      notification.error({
        message: t('apps.detail.publish.error', '发布失败'),
        description: t('apps.detail.publish.errorDescription', '发布应用时发生错误，请稍后重试'),
        duration: 3
      });
    } finally {
      setIsPublishing(false);
      setDropdownVisible(false);
    }
  };

  // 添加导出PDF功能
  const handleExportPDF = async (type: 'requirements' | 'userStories' | 'techSpecs') => {
    try {
      // 获取token
      const token = localStorage.getItem('token');
      if (!token) {
        notification.error({
          message: '未登录',
          description: '请先登录后再导出PDF',
          duration: 3
        });
        return;
      }

      // 确定API名称
      let apiName = '';
      let title = '';
      
      switch (type) {
        case 'requirements':
          apiName = 'Requirement';
          title = '需求分析';
          break;
        case 'userStories':
          apiName = 'User Stories';
          title = '用户故事';
          break;
        case 'techSpecs':
          apiName = 'Specification';
          title = '技术规范';
          break;
      }

      if (!apiName) {
        notification.error({
          message: '导出失败',
          description: '无法确定导出类型',
          duration: 3
        });
        return;
      }

      // 显示加载提示
      message.loading({
        content: `正在生成PDF，请稍候...`,
        key: 'pdfExport',
        duration: 0
      });

      // 调用后端接口获取PDF
      const response = await axios.post(
        'https://ceshi.lightark.cc/api/v1/apps/get_app_doc_pdf',
        {
          app_id: Number(appId),
          api_name: apiName
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          responseType: 'arraybuffer'
        }
      );

      // 检查响应状态
      if (response.status !== 200) {
        throw new Error(`导出失败，HTTP状态码: ${response.status}`);
      }

      // 创建blob对象
      const blob = new Blob([response.data as ArrayBuffer], { type: 'application/pdf' });
      
      // 生成文件名
      const fileName = `${appData?.app_name || '应用'}-${title}.pdf`;
      
      // 创建临时链接并点击下载
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      
      // 关闭加载提示
      message.success({
        content: `${title}已成功导出为PDF`,
        key: 'pdfExport',
        duration: 2
      });
    } catch (error) {
      console.error('导出PDF时出错:', error);
      // 关闭加载提示并显示错误
      message.error({
        content: '导出PDF失败，请重试。如果问题持续存在，请联系技术支持。',
        key: 'pdfExport',
        duration: 2
      });
    }
  };

  // 添加显示API密钥的函数
  const handleShowApiKey = () => {
    if (appData?.publish_uuid) {
      notification.info({
        message: t('apps.detail.publish.apiKey.title', 'API 秘钥'),
        description: (
          <div>
            <div style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>app-{appData.publish_uuid}</div>
            <Space style={{ marginTop: '8px' }}>
              <Button size="small" onClick={() => {
                copy(`app-${appData.publish_uuid}`);
                message.success(t('apps.detail.apiKey.copied', '已复制'));
              }}>
                {t('apps.detail.apiKey.copy', '复制密钥')}
              </Button>
            </Space>
          </div>
        ),
        duration: 0,
      });
      setDropdownVisible(false);
    }
  };

  // 添加新的处理函数
  const handlePreviewHtml = async (recordId: number) => {
    try {
      // 添加到加载状态
      setLoadingPreviewIds(prev => [...prev, recordId]);
      
      const response = await axios.get<string>(
        `https://ceshi.lightark.cc/api/v1/run-records/${recordId}/preview_html`,
        {
          headers: {
            'Accept': 'application/json, text/plain, */*',
            'Accept-Language': 'zh',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          }
        }
      );
      
      // 创建一个新窗口并写入HTML内容
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(response.data);
        newWindow.document.close();
      } else {
        message.error('无法打开新窗口，请检查浏览器是否阻止弹出窗口');
      }
    } catch (error) {
      console.error('获取预览HTML失败:', error);
      message.error('获取预览HTML失败');
    } finally {
      // 从加载状态中移除
      setLoadingPreviewIds(prev => prev.filter(id => id !== recordId));
    }
  };

  // 检查回调地址是否已配置
  const checkCallbackUrl = async (publishUuid: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get<CallbackUrlResponse>(
        `https://ceshi.lightark.cc/api/v1/billing/callback-url/${publishUuid}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      setHasCallbackUrl(!!response.data?.callback_url);
      return !!response.data?.callback_url;
    } catch (error) {
      console.error('检查回调地址失败:', error);
      return false;
    }
  };

  // 自动配置回调地址
  const configureCallbackUrl = async (publishUuid: string) => {
    try {
      const token = localStorage.getItem('token');
      
      // 先检查是否已经配置过回调地址
      try {
        const checkResponse = await axios.get<{ callback_url: string }>(
          `https://ceshi.lightark.cc/api/v1/billing/callback-url/${publishUuid}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        // 如果已经配置过回调地址，直接返回 true
        if (checkResponse.data && checkResponse.data.callback_url) {
          return true;
        }
      } catch (error: any) {
        // 如果是 404 错误，说明未配置，继续执行配置逻辑
        if (error.response?.status !== 404) {
          throw error;
        }
      }

      // 如果没有配置过，则进行配置
      await axios.post(
        'https://ceshi.lightark.cc/api/v1/billing/callback-url',
        {
          publish_uuid: publishUuid,
          callback_url: 'https://www.iagent.cc/auth-api/billing-callback',
          status: 'active'
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return true;
    } catch (error) {
      console.error('配置回调地址失败:', error);
      return false;
    }
  };

  // 修改handlePublishToIAgent函数
  const handlePublishToIAgent = async () => {
    if (!appData?.publish_uuid) return;
    
    // 自动配置回调地址
    const configured = await configureCallbackUrl(appData.publish_uuid);
    if (!configured) {
      notification.error({
        message: t('apps.detail.publishToIAgent.error', '发布失败'),
        description: t('apps.detail.publishToIAgent.errorDescription', '配置回调地址失败，请稍后重试'),
        duration: 3
      });
      return;
    }

    // 打开发布配置Modal
    setIsPublishConfigModalOpen(true);
  };

  // 处理发布配置
  const handlePublishConfig = async (config: { name: string; description: string; icon: string }) => {
    if (!appData?.publish_uuid) return;

    try {
      const response = await axios.post(
        `https://admin.iagent.cc/api/custom-agent/create-by-uuid/${appData.publish_uuid}`,
        {
          name: config.name,
          description: config.description,
          // icon: `custom_agent/icons/${config.icon}.svg`
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200 && (response.data as any).code === 0) {
        notification.success({
          message: t('apps.detail.publishToIAgent.success', '发布成功'),
          description: t('apps.detail.publishToIAgent.successDescription', '已成功发布到iAgent'),
          duration: 3
        });
        setIsPublishConfigModalOpen(false);
      } else {
        throw new Error('发布失败');
      }
    } catch (error) {
      console.error('发布到iAgent失败:', error);
      notification.error({
        message: t('apps.detail.publishToIAgent.error', '发布失败'),
        description: t('apps.detail.publishToIAgent.errorDescription', '发布到iAgent时发生错误，请稍后重试'),
        duration: 3
      });
    } finally {
      setDropdownVisible(false);
    }
  };

  // 处理回调地址配置成功
  const handleCallbackUrlSuccess = async () => {
    if (appData?.publish_uuid) {
      await checkCallbackUrl(appData.publish_uuid);
      // 如果回调地址配置成功，继续发布流程
      handlePublishToIAgent();
    }
  };

  if (loading) {
    return (
      <div className="app-detail-container">
        <div className="loading-state">
          <div className="custom-spinner"></div>
          <p>{t('apps.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-detail-container">
        <div className="error-state">
          <h3>{t('apps.detail.error.title')}</h3>
          <p>{error}</p>
          {apiResponse && (
            <details>
              <summary>{t('apps.detail.error.apiResponse')}</summary>
              <pre className="api-response">{JSON.stringify(apiResponse, null, 2)}</pre>
            </details>
          )}
          <button onClick={handleRetry} className="retry-button">
            {t('apps.detail.error.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-detail-container">
      <div className="app-detail-content">
        <div className="app-info-section">
          <div className="app-basic-info">
            <div className="app-header" style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between', paddingTop: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button 
                  className="back-button"
                  onClick={() => navigate('/apps')} 
                  title="返回应用列表"
                  aria-label="返回应用列表"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor"/>
                  </svg>
                  <span>返回</span>
                </button>
                <h1 className="app-title">{appData?.app_name}</h1>
              </div>
              <div className="app-action-buttons" style={{ marginLeft: 'auto', position: 'relative' }}>
                {/* 下拉菜单 */}
                <div className="dropdown-container" style={{ position: 'relative' }}>
                  <button 
                    className="run-app-button"
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 24px',
                      background: '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      boxShadow: '0 4px 8px rgba(37, 99, 235, 0.2)',
                      transition: 'all 0.3s ease',
                      minWidth: '140px'
                    }}
                    onClick={() => setDropdownVisible(!dropdownVisible)}
                  >
                    {t('apps.detail.run.button')}
                    <svg 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      style={{ marginLeft: '8px' }}
                    >
                      <path d="M7 10l5 5 5-5z" fill="currentColor"></path>
                    </svg>
                  </button>
                  
                  {/* 下拉内容 */}
                  {dropdownVisible && (
                    <div 
                      ref={dropdownRef}
                      className="dropdown-menu"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '4px',
                        background: 'white',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                        borderRadius: '8px',
                        padding: '8px 0',
                        width: '200px',
                        zIndex: 10
                      }}
                    >
                      {/* 发布按钮 */}
                      <button
                        className="dropdown-item"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          padding: '10px 16px',
                          borderRadius: '0',
                          border: 'none',
                          background: 'none',
                          textAlign: 'left' as 'left',
                          cursor: 'pointer',
                          transition: 'background 0.2s ease'
                        }}
                        onClick={handlePublishApp}
                        disabled={isPublishing}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.background = 'none';
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                          <path d="M5 4h14v2H5V4zm0 10h4v6h6v-6h4l-7-7-7 7z" fill="currentColor" />
                        </svg>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontSize: '14px', fontWeight: '500' }}>
                            {isPublishing ? t('apps.detail.publish.publishing') : t('apps.detail.publish.button')}
                          </span>
                          {appData?.published_at && (
                            <span style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                              {t('apps.detail.publish.lastPublished')} {getRelativeTime(appData.published_at)}
                            </span>
                          )}
                        </div>
                      </button>
                      
                      {/* API密钥按钮 */}
                      {appData?.publish_uuid && (
                        <button
                          className="dropdown-item"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            padding: '10px 16px',
                            borderRadius: '0',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left' as 'left',
                            cursor: 'pointer',
                            transition: 'background 0.2s ease'
                          }}
                          onClick={handleShowApiKey}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.background = 'none';
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                            <path d="M22 19h-6v-4h-2.68c-1.14 2.42-3.6 4-6.32 4-3.86 0-7-3.14-7-7s3.14-7 7-7c2.72 0 5.17 1.58 6.32 4H24v6h-2v4zm-4-2h2v-4h2v-2H11.94l-.23-.67C11.01 8.34 9.11 7 7 7c-2.76 0-5 2.24-5 5s2.24 5 5 5c2.11 0 4.01-1.34 4.71-3.33l.23-.67H18v4zM7 15c-1.65 0-3-1.35-3-3s1.35-3 3-3 3 1.35 3 3-1.35 3-3 3zm0-4c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z" fill="currentColor" />
                          </svg>
                          <span style={{ fontSize: '14px', fontWeight: '500' }}>
                            {t('apps.detail.apiKey.button', 'API Key')}
                          </span>
                        </button>
                      )}
                      
                      {/* 访问API文档按钮 */}
                      {appData?.publish_uuid && (
                        <button
                          className="dropdown-item"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            padding: '10px 16px',
                            borderRadius: '0',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left' as 'left',
                            cursor: 'pointer',
                            transition: 'background 0.2s ease'
                          }}
                          onClick={() => navigate(`/apps/api-docs/${appId}?workspace_id=${workspaceId}`)}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.background = 'none';
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                            <path d="M8 3h8c1.1 0 2 .9 2 2v16c0 1.1-.9 2-2 2H8c-1.1 0-2-.9-2-2V5c0-1.1.9-2 2-2zm0 2v16h8V5H8zm2 3h4v2h-4V8zm0 3h4v2h-4v-2zm0 3h4v2h-4v-2z" fill="currentColor" />
                          </svg>
                          <span style={{ fontSize: '14px', fontWeight: '500' }}>
                            {t('apps.detail.accessApi', '访问 API')}
                          </span>
                        </button>
                      )}
                      
                      {/* 打开应用按钮 */}
                      {appData?.publish_uuid && (
                        <>
                          <button
                            className="dropdown-item"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                              padding: '10px 16px',
                              borderRadius: '0',
                              border: 'none',
                              background: 'none',
                              textAlign: 'left' as 'left',
                              cursor: 'pointer',
                              transition: 'background 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6';
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLButtonElement).style.background = 'none';
                            }}
                            onClick={() => {
                              window.open(`https://ceshi.lightark.cc/api/v1/apps/access_html/${appData.publish_uuid}`, '_blank');
                              setDropdownVisible(false);
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                              <path d="M19 19H5V5h7V3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" fill="currentColor" />
                            </svg>
                            <span style={{ fontSize: '14px', fontWeight: '500' }}>
                              {t('apps.detail.openPublishedApp')}
                            </span>
                          </button>

                          <button
                            className="dropdown-item"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                              padding: '10px 16px',
                              borderRadius: '0',
                              border: 'none',
                              background: 'none',
                              textAlign: 'left' as 'left',
                              cursor: 'pointer',
                              transition: 'background 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6';
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLButtonElement).style.background = 'none';
                            }}
                            onClick={handlePublishToIAgent}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                              <path d="M19 19H5V5h7V3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" fill="currentColor" />
                            </svg>
                            <span style={{ fontSize: '14px', fontWeight: '500' }}>
                              {t('apps.detail.publishToIAgent', '部署到iAgent')}
                            </span>
                          </button>
                        </>
                      )}
                      
                      {/* 运行编辑应用按钮 */}
                      <button
                        className="dropdown-item"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          padding: '10px 16px',
                          borderRadius: '0',
                          border: 'none',
                          background: 'none',
                          textAlign: 'left' as 'left',
                          cursor: 'pointer',
                          transition: 'background 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.background = 'none';
                        }}
                        onClick={() => {
                          navigate(`/apps/canvas-chat?app_id=${appId}&workspace_id=${workspaceId}`);
                          setDropdownVisible(false);
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" fill="currentColor" />
                        </svg>
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>
                          {t('apps.detail.runEditMode')}
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <p className="app-description">{appData?.app_description}</p>
          </div>
          <div className="app-meta-row">
            <div className="meta-item">
              <span className="meta-label">{t('apps.detail.createTime')}：</span>
              <span className="meta-value">
                {new Date(appData?.create_time || '').toLocaleString()}
              </span>
            </div>
          </div>
        </div>
        <div className="tab-container">
          <div className="tab-header">
            <button 
              className={`tab-button ${activeTab === 'scene' ? 'active' : ''}`}
              onClick={() => setActiveTab('scene')}
            >
              {t('apps.detail.tabs.scene')}
            </button>
            <button 
              className={`tab-button ${activeTab === 'records' ? 'active' : ''}`}
              onClick={() => setActiveTab('records')}
            >
              {t('apps.detail.tabs.records')}
            </button>
          </div>
        </div>

        <div className="tab-content">
          {activeTab === 'scene' && (
            <div className="app-info">
              <TaskProgress appId={appId} handleExportPDF={handleExportPDF} />
            </div>
          )}

          {activeTab === 'records' && (
            <div className="run-records-section">
              <div className="run-records-table">
                <table>
                  <thead>
                    <tr>
                      <th>{t('apps.detail.record.id', 'ID')}</th>
                      <th>{t('apps.detail.record.time', '时间')}</th>
                      <th>{t('apps.detail.record.status')}</th>
                      <th>{t('apps.detail.record.actions', '操作')}</th>
                      <th>{t('apps.detail.record.preview', '预览')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runRecords.map(record => (
                      <tr key={record.record_id}>
                        <td>{record.record_id}</td>
                        <td>{new Date(record.run_time).toLocaleString()}</td>
                        <td>
                          <span className={`status-badge ${record.result}`}>
                            {record.result === 'success' ? t('apps.detail.record.statusSuccess', '成功') : 
                             record.result === 'error' ? t('apps.detail.record.statusError', '失败') :
                             record.result === 'started' ? t('apps.detail.record.statusStarted', '执行中') : record.result}
                          </span>
                        </td>
                        <td>
                          <div className="record-actions">
                            <button
                              className="view-detail-button"
                              onClick={() => {
                                setSelectedRecord(record);
                                setIsRecordDetailOpen(true);
                              }}
                            >
                              {t('apps.detail.record.view')}
                            </button>
                            <button
                              className="delete-button"
                              onClick={() => {
                                if (window.confirm(t('apps.detail.record.confirmDelete', '确定要删除此运行记录吗？'))) {
                                  handleDeleteRecord(record.record_id);
                                }
                              }}
                            >
                              {t('apps.app.menu.delete', '删除')}
                            </button>
                          </div>
                        </td>
                        <td>
                          <button
                            className="preview-button"
                            disabled={record.result !== 'success' || loadingPreviewIds.includes(record.record_id)}
                            onClick={() => handlePreviewHtml(record.record_id)}
                            style={{
                              padding: '4px 12px',
                              borderRadius: '4px',
                              border: '1px solid #d9d9d9',
                              background: record.result === 'success' ? '#1890ff' : '#f5f5f5',
                              color: record.result === 'success' ? 'white' : '#d9d9d9',
                              cursor: record.result === 'success' && !loadingPreviewIds.includes(record.record_id) ? 'pointer' : 'not-allowed',
                              transition: 'all 0.3s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px',
                              minWidth: '100px'
                            }}
                          >
                            {loadingPreviewIds.includes(record.record_id) ? (
                              <span className="loading-text">加载中</span>
                            ) : (
                              t('apps.detail.record.viewResult', '查看结果')
                            )}
                          </button>
                          <style>
                            {`
                              .loading-text::after {
                                content: '...';
                                display: inline-block;
                                animation: dots 1.5s steps(4, end) infinite;
                                width: 0;
                                overflow: hidden;
                                vertical-align: bottom;
                              }
                              
                              @keyframes dots {
                                0% { width: 0; }
                                20% { width: 0.25em; }
                                40% { width: 0.5em; }
                                60% { width: 0.75em; }
                                80% { width: 1em; }
                                100% { width: 0; }
                              }
                            `}
                          </style>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {runRecords.length === 0 && (
                  <div className="empty-state">{t('apps.detail.record.empty', '暂无运行记录')}</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <SaveVersionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveVersion}
        configs={agentOutput?.configs}
        appId={appId || ''}
      />

      <EditVersionModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        version={selectedVersionData}
        onSave={handleSaveEdit}
      />

      <RunModal
        isOpen={isRunModalOpen}
        onClose={() => setIsRunModalOpen(false)}
        onRun={handleRunVersion}
        versionData={selectedVersionData}
        isRunning={isRunning}
        runProgress={runProgress}
        setRunProgress={setRunProgress}
      />

      <RecordDetailModal
        isOpen={isRecordDetailOpen}
        onClose={() => setIsRecordDetailOpen(false)}
        record={selectedRecord}
      />

      <VersionDetailModal
        isOpen={isVersionDetailOpen}
        onClose={() => setIsVersionDetailOpen(false)}
        version={selectedVersionDetail}
      />
      
      {/* API文档已移至单独页面 */}
      
      {/* 添加回调地址配置Modal */}
      <CallbackUrlModal
        isOpen={isCallbackUrlModalOpen}
        onClose={() => setIsCallbackUrlModalOpen(false)}
        publishUuid={appData?.publish_uuid || ''}
        onSuccess={handleCallbackUrlSuccess}
      />
      
      {/* 添加发布配置Modal */}
      <PublishConfigModal
        isOpen={isPublishConfigModalOpen}
        onClose={() => setIsPublishConfigModalOpen(false)}
        appName={appData?.app_name || ''}
        appDescription={appData?.app_description || ''}
        onPublish={handlePublishConfig}
      />
    </div>
  );
};

export default AppDetail;
const styles = `
.app-detail-container {
  width: 100%;
  min-height: 100vh;
  padding: 80px 20px 20px; /* 添加顶部内边距，为固定头部留出空间 */
  background: #f5f5f5;
}

.tab-container {
  width: 100%;
  margin-bottom: 20px;
}

.tab-content {
  width: 100%;
  min-height: calc(100vh - 100px);
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.run-task-section {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.run-form {
  margin-bottom: 32px;
}

.run-form .form-item {
  margin-bottom: 24px;
}

.run-form label {
  display: block;
  margin-bottom: 8px;
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.task-input {
  width: 100%;
  min-height: 120px;
  padding: 16px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  resize: vertical;
  transition: all 0.3s;
}

.task-input:focus {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
  outline: none;
}

.run-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.run-actions button {
  padding: 8px 24px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.run-actions button.primary {
  background: #1890ff;
  color: white;
}

.run-actions button.primary:hover {
  background: #40a9ff;
}

.run-actions button:disabled {
  background: #f5f5f5;
  color: #d9d9d9;
  cursor: not-allowed;
}

.run-progress {
  margin-top: 32px;
  padding: 24px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.run-progress h3 {
  margin: 0 0 24px 0;
  font-size: 20px;
  color: #333;
  font-weight: 600;
  padding-bottom: 8px;
  border-bottom: 2px solid #1890ff;
  display: inline-block;
}

.progress-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.progress-bar-container {
  width: 100%;
  height: 24px;
  background: #f5f5f5;
  border-radius: 12px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: #1890ff;
  border-radius: 12px;
  transition: width 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-text {
  color: white;
  font-size: 14px;
  font-weight: 500;
}

.logs-section, .tasks-section, .run-result {
  background: #f8f8f8;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  margin-bottom: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  cursor: pointer;
  padding: 8px;
  background: #fff;
  border-radius: 4px;
}

.section-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.collapse-icon {
  color: #666;
  font-size: 12px;
  transition: transform 0.3s;
}

.logs-content, .tasks-preview, .result-content {
  max-height: 400px;
  overflow-y: auto;
  background: #fff;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
}

.log-item {
  padding: 8px 0;
  color: #666;
  font-family: monospace;
  font-size: 14px;
  line-height: 1.6;
  border-bottom: 1px solid #f0f0f0;
}

.log-item:last-child {
  border-bottom: none;
}

.task-preview-item {
  background: #fff;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
  margin-bottom: 16px;
}

.task-preview-item:last-child {
  margin-bottom: 0;
}

.task-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.task-name {
  font-weight: 500;
  color: #333;
  font-size: 16px;
}

.task-info {
  margin: 12px 0;
  color: #666;
}

.task-info p {
  margin: 4px 0;
}

.task-content {
  background: #f8f8f8;
  padding: 12px;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
}

.run-records-section {
  width: 100%;
  overflow-x: auto;
}

.run-records-table {
  width: 100%;
  min-width: 800px;
}

.scene-input-section {
  margin-top: 20px;
  padding: 20px;
  background: #f5f5f5;
  border-radius: 8px;
}

.scene-description-input {
  width: 100%;
  min-height: 100px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 14px;
}

.button-group {
  display: flex;
  gap: 12px;
}

.generate-button, .save-button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.generate-button {
  background-color: #1890ff;
  color: white;
}

.save-button {
  background-color: #52c41a;
  color: white;
}

.agent-output-section {
  margin-top: 24px;
  padding: 20px;
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
}

.agent-info {
  display: grid;
  gap: 20px;
}

.info-group {
  padding: 16px;
  background: #f8f8f8;
  border-radius: 6px;
}

.info-group h4 {
  margin: 0 0 12px 0;
  color: #333;
}

.code-block {
  background: #f0f0f0;
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 13px;
  margin: 8px 0;
}

.work-item {
  background: white;
  padding: 12px;
  border-radius: 4px;
  margin: 8px 0;
  border: 1px solid #eee;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  position: relative;
  background: white;
  padding: 24px;
  border-radius: 8px;
  width: 90%;
  max-width: 1000px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-form {
  margin: 20px 0;
}

.form-item {
  margin-bottom: 16px;
}

.form-item label {
  display: block;
  margin-bottom: 8px;
  color: #333;
}

.form-item input,
.form-item textarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.form-item textarea {
  min-height: 100px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.modal-actions button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.modal-actions button.primary {
  background-color: #1890ff;
  color: white;
}

.version-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.run-button {
  padding: 6px 12px;
  background-color: #1890ff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.run-button:hover {
  background-color: #40a9ff;
}

.task-input {
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;
}

.modal-actions button:disabled {
  background-color: #d9d9d9;
  cursor: not-allowed;
}

.version-card {
  cursor: pointer;
  transition: transform 0.2s;
}

.version-card:hover {
  transform: translateY(-2px);
}

.version-card .run-button {
  opacity: 0;
  transition: opacity 0.2s;
}

.version-card:hover .run-button {
  opacity: 1;
}

.run-records-table {
  width: 100%;
  overflow-x: auto;
}

.run-records-table table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.run-records-table th,
.run-records-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
}

.run-records-table th {
  background: #fafafa;
  font-weight: 500;
  color: #333;
}

.run-records-table tr:hover {
  background: #fafafa;
}

.view-detail-button {
  padding: 4px 12px;
  background: #1890ff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.view-detail-button:hover {
  background: #40a9ff;
}

.record-detail-modal {
  max-width: 1000px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.modal-header {
  position: sticky;
  top: 0;
  background: white;
  padding: 0 0 20px 0;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1;
}

.modal-header h2 {
  margin: 0;
  font-size: 20px;
  color: #333;
}

.close-button {
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 8px;
  line-height: 1;
  z-index: 2;
  transition: color 0.3s;
}

.close-button:hover {
  color: #666;
}

.record-detail-content {
  padding: 20px;
}

.detail-section {
  margin-bottom: 30px;
}

.detail-section h3 {
  margin: 0 0 16px;
  font-size: 18px;
  color: #333;
  font-weight: 500;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  background: #f8f8f8;
  padding: 16px;
  border-radius: 8px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-item label {
  font-size: 14px;
  color: #666;
}

.info-item span {
  font-size: 16px;
  color: #333;
}

.input-params {
  background: #f8f8f8;
  padding: 16px;
  border-radius: 8px;
}

.param-group {
  margin-bottom: 16px;
}

.param-group:last-child {
  margin-bottom: 0;
}

.param-group h4 {
  margin: 0 0 8px;
  font-size: 16px;
  color: #333;
}

.param-content {
  background: #fff;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid #eee;
}

.timeline-table {
  width: 100%;
  border-collapse: collapse;
  background: #f8f8f8;
  border-radius: 8px;
  overflow: hidden;
}

.timeline-table th,
.timeline-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.timeline-table th {
  background: #f0f0f0;
  font-weight: 500;
  color: #333;
}

.timeline-item {
  transition: background-color 0.3s;
}

.timeline-item:hover {
  background-color: #fff;
}

.timeline-time {
  white-space: nowrap;
  color: #666;
  font-size: 14px;
}

.timeline-status {
  width: 100px;
}

.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.success {
  background: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.status-badge.error {
  background: #fff2f0;
  color: #ff4d4f;
  border: 1px solid #ffccc7;
}

.status-badge.started {
  background: #e6f7ff;
  color: #1890ff;
  border: 1px solid #91d5ff;
}

.status-badge.log {
  background: #f5f5f5;
  color: #666;
  border: 1px solid #d9d9d9;
}

.task-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.task-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.task-name {
  font-weight: 500;
  color: #333;
}

.task-agent {
  font-size: 14px;
  color: #666;
}

.task-output {
  background: #fff;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid #eee;
  margin-top: 8px;
}

.log-content {
  color: #666;
  font-family: monospace;
  font-size: 14px;
}

.result-content {
  background: #f8f8f8;
  padding: 20px;
  border-radius: 8px;
  line-height: 1.6;
}

.result-content p {
  margin: 0 0 16px;
}

.result-content p:last-child {
  margin-bottom: 0;
}

.result-content strong {
  color: #333;
  font-weight: 500;
}

.result-content hr {
  border: none;
  border-top: 1px solid #eee;
  margin: 16px 0;
}

.version-detail-modal {
  max-width: 1000px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.version-detail-content {
  margin: 20px 0;
}

.version-description {
  background: #f8f8f8;
  padding: 16px;
  border-radius: 4px;
  margin: 0;
  line-height: 1.6;
}

.version-json {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.json-section {
  background: #f8f8f8;
  padding: 16px;
  border-radius: 4px;
}

.json-section h4 {
  margin: 0 0 12px 0;
  color: #333;
  font-size: 16px;
}

.json-section p {
  margin: 0;
  line-height: 1.6;
}

.code-files {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.code-file {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 4px;
  overflow: hidden;
}

.code-file h5 {
  margin: 0;
  padding: 8px 16px;
  background: #fafafa;
  border-bottom: 1px solid #eee;
  font-size: 14px;
  color: #333;
}

.code-content {
  margin: 0;
  padding: 16px;
  background: #f8f8f8;
  overflow-x: auto;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
}

.loading-spinner {
  display: inline-block;
  width: 24px;
  height: 24px;
  margin-right: 8px;
  border: 3px solid #f3f3f3;
  border-radius: 50%;
  border-top: 3px solid #1890ff;
  animation: spin 1s linear infinite;
  vertical-align: middle;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.generate-button.loading {
  opacity: 0.8;
  cursor: not-allowed;
}

.generate-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.generate-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
}

.error-message {
  margin-top: 16px;
  padding: 12px;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 4px;
}

.error-message p {
  color: #ff4d4f;
  margin: 0 0 8px 0;
}

.error-message details {
  margin-top: 8px;
}

.error-message summary {
  color: #666;
  cursor: pointer;
}

.error-message .api-response {
  margin: 8px 0 0 0;
  padding: 12px;
  background: #f8f8f8;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 12px;
  line-height: 1.5;
}

.run-progress {
  margin-top: 20px;
  padding: 15px;
  background: #f8f8f8;
  border-radius: 4px;
}

.progress-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.progress-bar-container {
  width: 100%;
  height: 20px;
  background-color: #f5f5f5;
  border-radius: 10px;
  margin-bottom: 16px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background-color: #1890ff;
  border-radius: 10px;
  transition: width 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-text {
  color: white;
  font-size: 12px;
  font-weight: 500;
  text-shadow: 0 0 2px rgba(0, 0, 0, 0.2);
}

.run-result {
  margin-top: 24px;
  padding: 20px;
  background: #f8f8f8;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  margin-bottom: 24px;
}

.result-content {
  max-height: 500px;
  overflow-y: auto;
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  border: 1px solid #e8e8e8;
}

.result-content h1,
.result-content h2,
.result-content h3 {
  margin: 16px 0 8px;
  color: #333;
  font-weight: 600;
}

.result-content h1 {
  font-size: 24px;
}

.result-content h2 {
  font-size: 20px;
}

.result-content h3 {
  font-size: 16px;
}

.result-content p {
  margin: 8px 0;
  color: #333;
}

.result-content strong {
  font-weight: 600;
  color: #333;
}

.result-content em {
  font-style: italic;
  color: #666;
}

.result-content ul {
  margin: 8px 0;
  padding-left: 20px;
}

.result-content li {
  margin: 4px 0;
  color: #333;
}

.result-content hr {
  margin: 16px 0;
  border: none;
  border-top: 1px solid #e8e8e8;
}

.task-content {
  background: #f8f8f8;
  padding: 16px;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  min-height: 100px;
  max-height: 300px;
  overflow-y: auto;
}

.logs-content {
  max-height: 300px;
  overflow-y: auto;
  background: #f8f8f8;
  padding: 16px;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.6;
}

.tasks-preview {
  max-height: 500px;
  overflow-y: auto;
  margin: 16px 0;
}

.task-detail-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
}

.task-detail-content {
  margin: 20px 0;
}

.detail-section {
  margin-bottom: 24px;
}

.detail-section h3 {
  margin-bottom: 16px;
  color: #333;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-item label {
  color: #666;
  font-size: 14px;
}

.info-item span {
  color: #333;
  font-size: 16px;
}

.task-description {
  padding: 12px;
  background: #f8f8f8;
  border-radius: 4px;
  color: #333;
  line-height: 1.6;
}

.task-output {
  padding: 12px;
  background: #f8f8f8;
  border-radius: 4px;
}

.task-output pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
  color: #333;
  line-height: 1.6;
}

.task-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.task-table th,
.task-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
}

.task-table th {
  background: #fafafa;
  font-weight: 500;
  color: #333;
}

.task-table tr:hover {
  background: #f5f5f5;
}

.task-table .status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
}

.task-table .status-badge.started {
  background: #e6f7ff;
  color: #1890ff;
  border: 1px solid #91d5ff;
}

.task-table .status-badge.completed {
  background: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.view-detail-button {
  padding: 4px 12px;
  background: #1890ff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: background-color 0.3s;
}

.view-detail-button:hover {
  background: #40a9ff;
}

.progress-tasks {
  margin: 20px 0;
  max-height: 400px;
  overflow-y: auto;
}

.run-modal {
  max-width: 800px;
  width: 90%;
  position: relative;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.modal-form {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
}

.task-input {
  height: 80px;
  resize: none;
}

.progress-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.progress-logs {
  background: #f8f8f8;
  border-radius: 4px;
  padding: 8px;
  max-height: 80px;
  overflow-y: auto;
}

.log-item {
  padding: 4px 8px;
  font-size: 14px;
  color: #666;
}

.run-result {
  flex: 1;
  background: #f8f8f8;
  border-radius: 4px;
  padding: 16px;
}

.result-content {
  margin-top: 8px;
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
}

.tasks-preview {
  margin: 16px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.task-preview-item {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 12px;
}

.task-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.task-name {
  font-weight: 500;
  color: #333;
}

.status-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.status-badge.started {
  background: #e6f7ff;
  color: #1890ff;
  border: 1px solid #91d5ff;
}

.status-badge.completed {
  background: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.task-content {
  font-size: 14px;
  line-height: 1.6;
  color: #666;
  white-space: pre-wrap;
  background: #fafafa;
  padding: 8px;
  border-radius: 4px;
  max-height: 100px;
  overflow-y: auto;
}

.version-actions {
  display: flex;
  gap: 8px;
  flex-direction: row;
  align-items: center;
}

.action-button {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
  color: white;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.edit-button {
  background-color: #52c41a;
}

.edit-button:hover {
  background-color: #73d13d;
}

.delete-button {
  background-color: #ff4d4f;
}

.delete-button:hover {
  background-color: #ff7875;
}

.run-button {
  background-color: #1890ff;
}

.run-button:hover {
  background-color: #40a9ff;
}

.tab-container {
  margin-bottom: 20px;
}

.tab-header {
  display: flex;
  gap: 8px;
  border-bottom: 1px solid #e8e8e8;
  margin-bottom: 20px;
}

.tab-button {
  padding: 12px 24px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 16px;
  color: #666;
  position: relative;
  transition: all 0.3s;
}

.tab-button.active {
  color: #1890ff;
  font-weight: 500;
}

.tab-button.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background: #1890ff;
}

.tab-button:hover {
  color: #40a9ff;
}

.tab-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  min-height: 400px;
}

.scene-info {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.scene-info-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
  padding: 20px;
  background: #f8f8f8;
  border-radius: 8px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-item label {
  color: #666;
  font-size: 14px;
}

.info-item span {
  color: #333;
  font-size: 16px;
}

.versions-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.run-records-table {
  width: 100%;
  overflow-x: auto;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 16px;
  background: #f8f8f8;
  border-radius: 8px;
}

.records-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.run-progress {
  background: #f8f8f8;
  border-radius: 8px;
  padding: 20px;
}

.progress-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.progress-bar-container {
  width: 100%;
  height: 20px;
  background-color: #f0f0f0;
  border-radius: 10px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background-color: #1890ff;
  border-radius: 10px;
  transition: width 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-text {
  color: white;
  font-size: 12px;
  font-weight: 500;
}

.tasks-preview {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.task-preview-item {
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px;
}

.task-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.task-name {
  font-weight: 500;
  color: #333;
}

.task-content {
  background: #f8f8f8;
  padding: 12px;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
}

.run-result {
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px;
}

.run-result h3 {
  margin: 0 0 12px 0;
  color: #333;
}

.result-content {
  background: #f8f8f8;
  padding: 12px;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
}

.run-history {
  background: white;
  border-radius: 8px;
  padding: 20px;
}

.run-history h3 {
  margin: 0 0 16px 0;
  color: #333;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.started {
  background: #e6f7ff;
  color: #1890ff;
  border: 1px solid #91d5ff;
}

.status-badge.completed {
  background: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.status-badge.error {
  background: #fff2f0;
  color: #ff4d4f;
  border: 1px solid #ffccc7;
}

.status-badge.success {
  background: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.logs-section, .tasks-section {
  margin: 16px 0;
  background: white;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #f0f0f0;
}

.logs-section h4, .tasks-section h4 {
  margin: 0 0 12px 0;
  color: #333;
}

.logs-content {
  max-height: 200px;
  overflow-y: auto;
  background: #f8f8f8;
  padding: 12px;
  border-radius: 4px;
}

.log-item {
  padding: 4px 0;
  color: #666;
  font-family: monospace;
}

.task-info {
  margin: 8px 0;
  color: #666;
}

.task-info p {
  margin: 4px 0;
}

.task-content pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 14px;
  line-height: 1.5;
}

.run-progress h3 {
  margin: 0 0 16px 0;
  color: #333;
}

.record-actions {
  display: flex;
  gap: 8px;
}

.delete-button {
  padding: 4px 12px;
  background: #ff4d4f;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.delete-button:hover {
  background: #ff7875;
}

.view-detail-button {
  padding: 4px 12px;
  background: #1890ff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.view-detail-button:hover {
  background: #40a9ff;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 12px;
  background: #f8f8f8;
  border-radius: 4px;
  margin-bottom: 8px;
}

.section-header:hover {
  background: #f0f0f0;
}

.section-header h4 {
  margin: 0;
  color: #333;
}

.collapse-icon {
  color: #666;
  font-size: 12px;
  transition: transform 0.3s;
}

.logs-section, .tasks-section, .run-result {
  margin: 16px 0;
  background: white;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #f0f0f0;
}

.logs-content, .tasks-preview, .result-content {
  max-height: 300px;
  overflow-y: auto;
  transition: all 0.3s ease;
}

.logs-content.collapsed, .tasks-preview.collapsed, .result-content.collapsed {
  max-height: 0;
  overflow: hidden;
}

.task-preview-item {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f8f8;
  border-radius: 4px;
  border: 1px solid #f0f0f0;
}

.task-preview-item:last-child {
  margin-bottom: 0;
}

.run-sheet {
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 800px;
  height: 100vh;
  background: white;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
}

.run-sheet-header {
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.run-sheet-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.version-select {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.version-select select {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
}

.run-form {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.task-input {
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  resize: vertical;
  font-size: 14px;
}

.run-actions {
  padding: 16px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.run-actions button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.run-actions button.primary {
  background: #1890ff;
  color: white;
}

.run-actions button:disabled {
  background: #f5f5f5;
  color: #d9d9d9;
  cursor: not-allowed;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 4px;
  line-height: 1;
}

.close-button:hover {
  color: #333;
}

.execution-in-progress {
  text-align: center;
  padding: 20px;
  color: #666;
  font-size: 14px;
  background: #f8f8f8;
  border-radius: 8px;
  margin-top: 16px;
}

/* 运行记录详情样式 */
.record-detail-modal .detail-section h3 {
  margin: 0 0 16px 0;
  font-size: 20px;
  color: #333;
  font-weight: 600;
  padding-bottom: 8px;
  border-bottom: 2px solid #1890ff;
  display: inline-block;
}

.record-detail-modal .detail-section {
  margin-bottom: 40px;
  background: #fff;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.record-detail-modal .result-content {
  max-height: 500px; /* 增加运行结果的显示高度 */
  overflow-y: auto;
  background: #f8f8f8;
  padding: 20px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  border: 1px solid #e8e8e8;
}

.record-detail-modal .input-params {
  background: #f8f8f8;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
}

.record-detail-modal .execution-timeline {
  background: #f8f8f8;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
}

.record-detail-modal .timeline-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.record-detail-modal .timeline-table th {
  background: #f0f0f0;
  padding: 12px 16px;
  text-align: left;
  font-weight: 500;
  color: #333;
}

.record-detail-modal .timeline-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.record-detail-modal .timeline-item:hover {
  background: #fafafa;
}

/* 运行记录详情表格增强边框 */
.record-detail-modal .timeline-table {
  border: 1.5px solid #bfc8d8;
  border-radius: 8px;
  overflow: hidden;
  border-collapse: collapse;
}

.record-detail-modal .timeline-table th,
.record-detail-modal .timeline-table td {
  border: 1.5px solid #bfc8d8;
}

/* 运行结果 markdown 表格增强边框 */
.markdown-container table {
  border: 1.5px solid #bfc8d8;
  border-radius: 8px;
  border-collapse: collapse;
  width: 100%;
  margin: 12px 0;
  background: #fff;
}
.markdown-container th,
.markdown-container td {
  border: 1.5px solid #bfc8d8;
  padding: 8px 12px;
  text-align: left;
}
.markdown-container th {
  background: #f5f7fa;
}

.export-buttons-container {
  margin-bottom: 20px;
  padding: 15px 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

/* 适应移动端的响应式布局 */
@media screen and (max-width: 768px) {
  .export-buttons-container {
    justify-content: center;
    padding: 12px 15px;
  }
  
  .export-button {
    width: 100%;
    justify-content: center;
  }
}

.export-button {
  padding: 8px 16px;
  background-color: #1890ff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.045);
}

.export-button:hover {
  background-color: #40a9ff;
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(24, 144, 255, 0.2);
}

.export-button:active {
  background-color: #096dd9;
  transform: translateY(0);
}

.run-history {
  background: white;
  border-radius: 8px;
  padding: 20px;
}

.custom-spinner {
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 4px solid rgba(24, 144, 255, 0.2);
  border-radius: 50%;
  border-left-color: #1890ff;
  animation: customSpin 1s linear infinite;
}

@keyframes customSpin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.back-button {
  display: inline-flex !important;
  align-items: center !important;
  font-size: 26px !important;
  font-weight: bold !important;
  color: #2563eb !important;
  background: #e8f0fe !important;
  border: none !important;
  border-radius: 24px !important;
  padding: 8px 18px !important;
  box-shadow: 0 2px 8px rgba(37,99,235,0.10) !important;
  cursor: pointer !important;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s !important;
  margin-right: 20px !important;
}
.back-button:hover {
  background: #2563eb !important;
  color: #fff !important;
  box-shadow: 0 4px 16px rgba(37,99,235,0.18) !important;
}
.back-button svg {
  margin-right: 8px !important;
  font-size: 30px !important;
}
`;

const styleSheet = document.createElement('style');
styleSheet.type = 'text/css';
styleSheet.innerText = styles;
document.head.appendChild(styleSheet); 
